import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input
} from '@angular/core';
import { SkeletonBreakpointService } from './skeleton-breakpoint.service';
import { ResponsiveProp } from './skeleton-grid.types';
import { resolveResponsiveProp } from './skeleton-grid.utils';

@Component({
  selector: 'umss-skeleton-grid',
  standalone: true,
  templateUrl: './skeleton-grid.component.html',
  styleUrl: './skeleton-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--skeleton-grid-cols]': 'computedCols()',
    '[style.--skeleton-grid-gap]': 'computedGap()',
    '[style.--skeleton-item-col]': 'computedCol()',
    '[style.--skeleton-item-row]': 'computedRow()'
  }
})
export class UmssSkeletonGridComponent {
  private responsiveService = inject(SkeletonBreakpointService);

  /**
   * The number of columns for the grid. Can be a number (e.g. 12),
   * a string grid-template (e.g. '300px 1fr'), or an object defining
   * responsive values across breakpoints.
   */
  columns = input<ResponsiveProp>(12);

  /**
   * The gap between grid items. Default is '16px' / 'var(--spacing-4)'.
   */
  gap = input<ResponsiveProp>('var(--spacing-4, 16px)');

  computedCols = computed(() => {
    const bp = this.responsiveService.activeBreakpoint();
    const cols = resolveResponsiveProp(this.columns(), bp, 12);

    // If cols is a pure number or numeric string, convert to a fractional representation
    if (typeof cols === 'number' || !isNaN(Number(cols))) {
      return `repeat(${cols}, minmax(0, 1fr))`;
    }
    // Return explicit grid-template strings immediately
    return cols;
  });

  computedGap = computed(() => {
    const bp = this.responsiveService.activeBreakpoint();
    return resolveResponsiveProp(this.gap(), bp, 'var(--spacing-4, 16px)');
  });

  // --- Grid Item Layout API (for nesting) ---
  colSpan = input<ResponsiveProp>();
  colStart = input<ResponsiveProp>();
  rowSpan = input<ResponsiveProp>();
  rowStart = input<ResponsiveProp>();

  computedCol = computed(() => {
    const bp = this.responsiveService.activeBreakpoint();
    const start = resolveResponsiveProp(this.colStart(), bp, '');
    const span = resolveResponsiveProp(this.colSpan(), bp, '');

    if (!start && !span) return 'auto';
    if (typeof span === 'string' && span.includes('/')) return span;

    const startVal = start ? start : 'auto';
    const spanVal = span
      ? typeof span === 'number' || !isNaN(Number(span))
        ? `span ${span}`
        : span
      : 'auto';
    return `${startVal} / ${spanVal}`;
  });

  computedRow = computed(() => {
    const bp = this.responsiveService.activeBreakpoint();
    const start = resolveResponsiveProp(this.rowStart(), bp, '');
    const span = resolveResponsiveProp(this.rowSpan(), bp, '');

    if (!start && !span) return 'auto';
    if (typeof span === 'string' && span.includes('/')) return span;

    const startVal = start ? start : 'auto';
    const spanVal = span
      ? typeof span === 'number' || !isNaN(Number(span))
        ? `span ${span}`
        : span
      : 'auto';
    return `${startVal} / ${spanVal}`;
  });
}
